import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  Add,
  Edit,
  RateReview,
  CheckCircle,
  Cancel,
  Archive,
  ContentCopy,
  Description,
  Visibility,
} from '@mui/icons-material';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

interface Document {
  id: string;
  documentNumber: string;
  title: string;
  description?: string;
  documentType: string;
  majorVersion: number;
  minorVersion: number;
  status: string;
  content?: string;
  itemId?: string;
  routeId?: string;
  createdBy?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  effectiveDate?: string;
  expiryDate?: string;
  tags?: string[];
  revisionNotes?: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const statusColor: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'success',
  obsolete: 'error',
  rejected: 'error',
};

const typeLabels: Record<string, string> = {
  work_instruction: 'Work Instruction',
  sop: 'SOP',
  specification: 'Specification',
  form: 'Form',
  policy: 'Policy',
  drawing: 'Drawing',
};

export default function DocumentControl() {
  const [tab, setTab] = useState(0);
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({
    documentNumber: '',
    title: '',
    description: '',
    documentType: 'sop',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // View dialog
  const [viewDoc, setViewDoc] = useState<Document | null>(null);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // New Revision dialog
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionTargetId, setRevisionTargetId] = useState<string | null>(null);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [revisionVersion, setRevisionVersion] = useState('');
  const [revisionSubmitting, setRevisionSubmitting] = useState(false);

  const fetchDocs = useCallback(async (statusFilter?: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/api/v1/quality/document-control${params}`, {
        headers: authHeaders(),
      });
      const data = res.data?.data || res.data || [];
      setDocs(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch documents');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleCreate = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/v1/quality/document-control`, newDoc, {
        headers: authHeaders(),
      });
      setCreateOpen(false);
      setNewDoc({ documentNumber: '', title: '', description: '', documentType: 'sop', content: '' });
      await fetchDocs();
      setSnackbar('Document created successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForReview = async (id: string) => {
    try {
      await axios.post(`${API}/api/v1/quality/document-control/${id}/submit-review`, {}, {
        headers: authHeaders(),
      });
      await fetchDocs();
      setSnackbar('Document submitted for review');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit document for review');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await axios.post(`${API}/api/v1/quality/document-control/${id}/approve`, {}, {
        headers: authHeaders(),
      });
      await fetchDocs();
      setSnackbar('Document approved');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to approve document');
    }
  };

  const openRejectDialog = (id: string) => {
    setRejectTargetId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTargetId) return;
    setRejectSubmitting(true);
    try {
      await axios.post(
        `${API}/api/v1/quality/document-control/${rejectTargetId}/reject`,
        { reason: rejectReason },
        { headers: authHeaders() },
      );
      setRejectDialogOpen(false);
      setRejectTargetId(null);
      setRejectReason('');
      await fetchDocs();
      setSnackbar('Document rejected');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reject document');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const openRevisionDialog = (id: string) => {
    setRevisionTargetId(id);
    setRevisionNotes('');
    setRevisionVersion('');
    setRevisionDialogOpen(true);
  };

  const handleRevisionConfirm = async () => {
    if (!revisionTargetId) return;
    setRevisionSubmitting(true);
    try {
      const body: Record<string, string> = { revisionNotes };
      if (revisionVersion.trim()) {
        body.version = revisionVersion.trim();
      }
      await axios.post(
        `${API}/api/v1/quality/document-control/${revisionTargetId}/new-revision`,
        body,
        { headers: authHeaders() },
      );
      setRevisionDialogOpen(false);
      setRevisionTargetId(null);
      setRevisionNotes('');
      setRevisionVersion('');
      await fetchDocs();
      setSnackbar('New revision created');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create new revision');
    } finally {
      setRevisionSubmitting(false);
    }
  };

  const handleObsolete = async (id: string) => {
    try {
      await axios.post(`${API}/api/v1/quality/document-control/${id}/obsolete`, {}, {
        headers: authHeaders(),
      });
      await fetchDocs();
      setSnackbar('Document marked as obsolete');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to mark document as obsolete');
    }
  };

  const filteredDocs = (status?: string) =>
    status ? docs.filter((d) => d.status === status) : docs;

  const counts = {
    all: docs.length,
    draft: docs.filter((d) => d.status === 'draft').length,
    in_review: docs.filter((d) => d.status === 'in_review').length,
    approved: docs.filter((d) => d.status === 'approved').length,
    obsolete: docs.filter((d) => d.status === 'obsolete').length,
    rejected: docs.filter((d) => d.status === 'rejected').length,
  };

  const renderTable = (items: Document[]) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell>Doc #</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Effective</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No documents found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((doc) => (
              <TableRow key={doc.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{doc.documentNumber}</Typography>
                </TableCell>
                <TableCell>{doc.title}</TableCell>
                <TableCell>
                  <Chip label={typeLabels[doc.documentType] || doc.documentType} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{doc.majorVersion}.{doc.minorVersion}</TableCell>
                <TableCell>
                  <Chip label={doc.status.replace(/_/g, ' ')} size="small" color={statusColor[doc.status] || 'default'} />
                </TableCell>
                <TableCell>
                  {doc.effectiveDate ? new Date(doc.effectiveDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  {new Date(doc.updatedAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => setViewDoc(doc)}>
                      <Visibility />
                    </IconButton>
                  </Tooltip>

                  {/* draft: submit for review */}
                  {doc.status === 'draft' && (
                    <Tooltip title="Submit for Review">
                      <IconButton size="small" color="info" onClick={() => handleSubmitForReview(doc.id)}>
                        <RateReview />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* rejected: edit (re-submit) */}
                  {doc.status === 'rejected' && (
                    <Tooltip title="Revise & Resubmit">
                      <IconButton size="small" color="info" onClick={() => handleSubmitForReview(doc.id)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* in_review: approve / reject */}
                  {doc.status === 'in_review' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => handleApprove(doc.id)}>
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => openRejectDialog(doc.id)}>
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {/* approved: new revision / archive */}
                  {doc.status === 'approved' && (
                    <>
                      <Tooltip title="New Revision">
                        <IconButton size="small" color="primary" onClick={() => openRevisionDialog(doc.id)}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark Obsolete">
                        <IconButton size="small" color="error" onClick={() => handleObsolete(doc.id)}>
                          <Archive />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Document Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage work instructions, SOPs, and controlled documents with versioning and approval workflows.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            New Document
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetchDocs()} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: counts.all, color: 'primary.main' },
          { label: 'Draft', value: counts.draft, color: 'text.secondary' },
          { label: 'In Review', value: counts.in_review, color: 'warning.main' },
          { label: 'Approved', value: counts.approved, color: 'success.main' },
          { label: 'Obsolete', value: counts.obsolete, color: 'error.main' },
          { label: 'Rejected', value: counts.rejected, color: 'error.main' },
        ].map((c) => (
          <Grid item xs={6} sm={2} key={c.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h5" sx={{ color: c.color }}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="All" />
        <Tab label={`Draft (${counts.draft})`} />
        <Tab label={`In Review (${counts.in_review})`} />
        <Tab label={`Approved (${counts.approved})`} />
        <Tab label={`Rejected (${counts.rejected})`} />
      </Tabs>

      <TabPanel value={tab} index={0}>
        {loading ? (
          <Box>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />)}</Box>
        ) : renderTable(filteredDocs())}
      </TabPanel>
      <TabPanel value={tab} index={1}>{renderTable(filteredDocs('draft'))}</TabPanel>
      <TabPanel value={tab} index={2}>{renderTable(filteredDocs('in_review'))}</TabPanel>
      <TabPanel value={tab} index={3}>{renderTable(filteredDocs('approved'))}</TabPanel>
      <TabPanel value={tab} index={4}>{renderTable(filteredDocs('rejected'))}</TabPanel>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Document Number"
              fullWidth
              value={newDoc.documentNumber}
              onChange={(e) => setNewDoc({ ...newDoc, documentNumber: e.target.value })}
              placeholder="e.g., SOP-QA-001"
            />
            <TextField
              label="Title"
              fullWidth
              value={newDoc.title}
              onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                label="Document Type"
                value={newDoc.documentType}
                onChange={(e) => setNewDoc({ ...newDoc, documentType: e.target.value })}
              >
                <MenuItem value="sop">SOP</MenuItem>
                <MenuItem value="work_instruction">Work Instruction</MenuItem>
                <MenuItem value="specification">Specification</MenuItem>
                <MenuItem value="form">Form</MenuItem>
                <MenuItem value="policy">Policy</MenuItem>
                <MenuItem value="drawing">Drawing</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newDoc.description}
              onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
            />
            <TextField
              label="Content"
              fullWidth
              multiline
              rows={4}
              value={newDoc.content}
              onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
              placeholder="Document content / work instructions..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting || !newDoc.documentNumber || !newDoc.title}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewDoc} onClose={() => setViewDoc(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            {viewDoc?.documentNumber} - {viewDoc?.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewDoc && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography>{typeLabels[viewDoc.documentType] || viewDoc.documentType}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Version</Typography>
                  <Typography>{viewDoc.majorVersion}.{viewDoc.minorVersion}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>
                    <Chip label={viewDoc.status.replace(/_/g, ' ')} size="small" color={statusColor[viewDoc.status] || 'default'} />
                  </Box>
                </Grid>
                {viewDoc.effectiveDate && (
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Effective Date</Typography>
                    <Typography>{new Date(viewDoc.effectiveDate).toLocaleDateString()}</Typography>
                  </Grid>
                )}
                {viewDoc.approvedAt && (
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Approved</Typography>
                    <Typography>{new Date(viewDoc.approvedAt).toLocaleDateString()}</Typography>
                  </Grid>
                )}
              </Grid>
              {viewDoc.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography>{viewDoc.description}</Typography>
                </Box>
              )}
              {viewDoc.content && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Content</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13 }}>
                    {viewDoc.content}
                  </Paper>
                </Box>
              )}
              {viewDoc.revisionNotes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Revision Notes</Typography>
                  <Typography variant="body2">{viewDoc.revisionNotes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDoc(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Rejection Reason"
              fullWidth
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Provide a detailed reason for rejection (minimum 10 characters)..."
              helperText={`${rejectReason.length} characters — minimum 10 required`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={rejectSubmitting || rejectReason.trim().length < 10}
          >
            {rejectSubmitting ? 'Rejecting...' : 'Reject Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Revision Dialog */}
      <Dialog open={revisionDialogOpen} onClose={() => setRevisionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Revision</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Revision Notes"
              fullWidth
              multiline
              rows={4}
              value={revisionNotes}
              onChange={(e) => setRevisionNotes(e.target.value)}
              placeholder="Describe what changed in this revision..."
              required
            />
            <TextField
              label="New Version (optional)"
              fullWidth
              value={revisionVersion}
              onChange={(e) => setRevisionVersion(e.target.value)}
              placeholder="e.g., 2.0 — leave blank to auto-increment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRevisionConfirm}
            disabled={revisionSubmitting || !revisionNotes.trim()}
          >
            {revisionSubmitting ? 'Creating...' : 'Create Revision'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
